// Product Media data layer (Phase 4, P4-WP10) — photos/PDFs in Supabase Storage,
// video as embed URLs. The SINGLE interface between the app and public.product_media
// + the private `product-media` Storage bucket
// (supabase/migrations/20260703103000_catalogue_media.sql).
//
// STORAGE MODEL (first use in the project):
//   • Bucket is PRIVATE. Objects live at  <company_id>/<product_id>/<uuid>.<ext>.
//     storage.objects RLS lets a user touch an object only when its first path
//     segment is one of THEIR companies — the path-based analogue of table RLS.
//     So uploads MUST use the company-prefixed path (built here from
//     current_company_id()); the policy rejects anything else.
//   • We store the object PATH (storage_path), never a URL. Display URLs are
//     time-limited SIGNED URLs derived at read time — no host/key is hardcoded.
//   • Video is never uploaded — it's an external embed URL (external_url).
//
// company_id/product_id are owned + frozen by DB triggers; we never send them.

import { supabase } from "./supabaseClient";

const BUCKET = "product-media";
const T = "product_media";
const SIGNED_TTL = 3600; // 1h

export const MEDIA_KINDS = [
  { value: "photo", label: "Photo" },
  { value: "video", label: "Video (embed URL)" },
  { value: "pdf", label: "Spec sheet (PDF)" },
];

const MEDIA_SELECT =
  "id, product_id, kind, storage_path, external_url, title, mime_type, size_bytes, position, is_primary, created_at";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}
function toError(error, fallback) {
  const code = error && error.code;
  let message;
  switch (code) {
    case "23505": message = "That media is already the primary for this product."; break;
    case "23514": message = "Invalid media — a photo/PDF needs a file, a video needs a URL."; break;
    case "23503": message = "That product does not exist."; break;
    case "42501": message = "You do not have access to this product's media."; break;
    case "PGRST116": message = "Media not found, or you do not have access to it."; break;
    default: message = (error && error.message) || fallback;
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

async function currentCompanyId() {
  const { data, error } = await client().rpc("current_company_id");
  if (error) throw toError(error, "Could not determine your company");
  if (!data) throw new Error("You must belong to a company to manage product media.");
  return data;
}
const rid = () => (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`);
function kindFor(file) {
  if (file?.type === "application/pdf") return "pdf";
  if (file?.type?.startsWith("image/")) return "photo";
  const e = new Error("Only images and PDFs can be uploaded. Add video as an embed URL."); e.code = "BAD_KIND"; throw e;
}
const extFor = (file, kind) => {
  const fromName = file?.name?.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
  return fromName || (kind === "pdf" ? "pdf" : "jpg");
};

// ── Reads (display-ready: photo/pdf resolved to signed URLs) ─────────────────

/**
 * Media for a product, ordered, each with a resolved display `url`
 * (signed URL for photo/pdf, external_url for video). RLS-scoped.
 * @param {string} productId
 * @returns {Promise<Array>}
 */
export async function getMedia(productId) {
  const c = client();
  const { data, error } = await c.from(T).select(MEDIA_SELECT)
    .eq("product_id", productId).order("position", { ascending: true }).order("created_at", { ascending: true });
  if (error) throw toError(error, "Could not load media");
  const rows = data || [];
  const paths = rows.filter((r) => r.storage_path).map((r) => r.storage_path);
  const signed = {};
  if (paths.length) {
    const { data: urls } = await c.storage.from(BUCKET).createSignedUrls(paths, SIGNED_TTL);
    (urls || []).forEach((u) => { if (u.path && u.signedUrl) signed[u.path] = u.signedUrl; });
  }
  return rows.map((r) => ({ ...r, url: r.kind === "video" ? r.external_url : (signed[r.storage_path] || null) }));
}

/** A fresh signed URL for a single stored object (e.g. full-screen open). */
export async function signedUrl(storagePath, ttl = SIGNED_TTL) {
  const { data, error } = await client().storage.from(BUCKET).createSignedUrl(storagePath, ttl);
  if (error) throw toError(error, "Could not open media");
  return data?.signedUrl || null;
}

// ── Writes ───────────────────────────────────────────────────────────────────

/**
 * Upload a photo or PDF to the company-prefixed Storage path and record it.
 * The first media added to a product becomes its primary.
 * @param {string} productId @param {File|Blob} file @param {{title?:string}} [opts]
 * @returns {Promise<string>} new media id.
 */
export async function uploadMedia(productId, file, { title } = {}) {
  const c = client();
  const kind = kindFor(file);
  const companyId = await currentCompanyId();
  const path = `${companyId}/${productId}/${rid()}.${extFor(file, kind)}`;
  // Deliberately NO cacheControl override here, unlike expenses.js's uploadReceipt().
  // Product photos are marketing images re-fetched on every catalogue render — long
  // edge-caching (Supabase Storage's platform default, Cache-Control: max-age=3600)
  // is a genuine performance win here, not a risk. Receipts are the opposite: the
  // highest-sensitivity bucket this app holds, where that same caching means a
  // deleted file can keep being served for up to an hour after real backend erasure
  // (CC22 Phase 2). The asymmetry between these two upload calls is intentional —
  // do not "tidy" them into consistency in either direction.
  const { error: upErr } = await c.storage.from(BUCKET).upload(path, file, { contentType: file.type, upsert: false });
  if (upErr) throw toError(upErr, "Could not upload the file");

  const isFirst = ((await c.from(T).select("id", { count: "exact", head: true }).eq("product_id", productId)).count ?? 0) === 0;
  const { data, error } = await c.from(T)
    .insert({ product_id: productId, kind, storage_path: path, title: title || null, mime_type: file.type, size_bytes: file.size, is_primary: isFirst })
    .select("id").single();
  if (error) { await c.storage.from(BUCKET).remove([path]); throw toError(error, "Could not save the media record"); } // roll back the orphan object
  return data.id;
}

/**
 * Record a video by its embed URL (no upload). First media becomes primary.
 * @param {string} productId @param {string} externalUrl @param {{title?:string}} [opts]
 * @returns {Promise<string>} new media id.
 */
export async function addVideo(productId, externalUrl, { title } = {}) {
  const c = client();
  const url = String(externalUrl || "").trim();
  if (!url) { const e = new Error("Enter a video URL."); e.code = "BAD_URL"; throw e; }
  const isFirst = ((await c.from(T).select("id", { count: "exact", head: true }).eq("product_id", productId)).count ?? 0) === 0;
  const { data, error } = await c.from(T)
    .insert({ product_id: productId, kind: "video", external_url: url, title: title || null, is_primary: isFirst })
    .select("id").single();
  if (error) throw toError(error, "Could not save the video");
  return data.id;
}

/** Update editable fields (title). */
export async function updateMedia(id, { title }) {
  const { error } = await client().from(T).update({ title: title ?? null }).eq("id", id).select("id").single();
  if (error) throw toError(error, "Could not update the media");
  return id;
}

/** Make one media the product's primary (unsets the previous primary first). */
export async function setPrimary(id) {
  const c = client();
  const { data: row, error: rErr } = await c.from(T).select("product_id").eq("id", id).maybeSingle();
  if (rErr) throw toError(rErr, "Could not update primary media");
  if (!row) throw toError({ code: "PGRST116" }, "Media not found");
  const { error: unset } = await c.from(T).update({ is_primary: false }).eq("product_id", row.product_id).eq("is_primary", true);
  if (unset) throw toError(unset, "Could not update primary media");
  const { error } = await c.from(T).update({ is_primary: true }).eq("id", id).select("id").single();
  if (error) throw toError(error, "Could not set primary media");
  return id;
}

/** Persist a new display order for a product's media. */
export async function reorderMedia(orderedIds = []) {
  const c = client();
  for (let i = 0; i < orderedIds.length; i++) {
    const { error } = await c.from(T).update({ position: i }).eq("id", orderedIds[i]);
    if (error) throw toError(error, "Could not reorder media");
  }
}

/**
 * Delete a media row and its Storage object. If it was primary, promotes the next.
 * @param {string} id @returns {Promise<void>}
 */
export async function deleteMedia(id) {
  const c = client();
  const { data: row, error: rErr } = await c.from(T).select("product_id, storage_path, is_primary").eq("id", id).maybeSingle();
  if (rErr) throw toError(rErr, "Could not delete media");
  if (!row) return;
  const { error } = await c.from(T).delete().eq("id", id);
  if (error) throw toError(error, "Could not delete media");
  if (row.storage_path) await c.storage.from(BUCKET).remove([row.storage_path]); // best-effort object cleanup
  if (row.is_primary) { // promote the next-in-order to primary
    const { data: next } = await c.from(T).select("id").eq("product_id", row.product_id).order("position", { ascending: true }).limit(1);
    if (next && next[0]) await c.from(T).update({ is_primary: true }).eq("id", next[0].id);
  }
}
